import type { ComputedRef } from 'vue';
import type { RouteRecordName } from 'vue-router';
import { computed } from 'vue';

import type { IFormSelectInputOption } from '@modules/forms/types';
import type { Permission } from '@modules/permissions/utils';
import type { EStaticPageName } from '@modules/static-pages/enums';
import type { IStaticPageConfig } from '@modules/static-pages/types';
import {
  TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION,
} from '@modules/general-settings/submodules/information/constants';
import { useI18n } from '@modules/i18n/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import {
  STATIC_PAGES_CONFIG,
} from '@modules/static-pages/constants';

export interface StaticPageConfigComposable {
  configAsEntries: [EStaticPageName, IStaticPageConfig][];
  staticPageOptions: ComputedRef<IFormSelectInputOption<EStaticPageName>[]>;
  getStaticPagePermission(page: EStaticPageName): Permission | undefined;
  getStaticPagesPermissions(): Permission[];
  getStaticPageNameByRoute(route: Maybe<RouteRecordName>): EStaticPageName | undefined;
}

export default function useStaticPageConfig(): StaticPageConfigComposable {
  const configAsEntries = Object.entries(STATIC_PAGES_CONFIG) as [EStaticPageName, IStaticPageConfig][];
  const { getTranslation } = useI18n();
  const { hasPermission } = usePermissionsStore();

  function getStaticPagePermission(page: EStaticPageName): Permission | undefined {
    return STATIC_PAGES_CONFIG[page]?.permission;
  }

  function getStaticPagesPermissions(): Permission[] {
    return configAsEntries.map(([, config]) => config.permission);
  }

  function getStaticPageNameByRoute(route: Maybe<RouteRecordName>): EStaticPageName | undefined {
    return configAsEntries.find(([, config]) => config.routeName === route)?.[0];
  }

  const staticPageOptions = computed<IFormSelectInputOption<EStaticPageName>[]>(() => configAsEntries
    .filter(([, config]) => hasPermission(config.permission).value)
    .map(([name]) => ({
      value: name,
      label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_INFORMATION, `PAGE_${name}`).value,
    })));

  return {
    getStaticPagePermission,
    getStaticPagesPermissions,
    getStaticPageNameByRoute,
    configAsEntries,
    staticPageOptions,
  };
}
